/**
 * The camera goodbye on app close — the contract between main and renderer.
 *
 * Why this exists: libgphoto2's goodbye (`gp_camera_exit` → Canon `SetRemoteMode(1)` +
 * `CloseSession`) only runs when the renderer calls `camera.disconnect()`. Every exit
 * path used to destroy the renderer before that could happen — the window `close`
 * event had no handler, `relaunch-app` called `app.exit(0)` (which skips every
 * handler), and `before-quit` fires after the window is already gone. The OS dropped
 * the USB pipe but the camera never heard it, so it stayed in remote mode with an open
 * PTP session and the next launch could not connect without a replug (#371 heals that
 * in software; this stops it happening on an intentional close).
 *
 * Protocol: main sends REQUEST with a requestId and how long it will wait; the renderer
 * releases its cameras and answers ACK with the same requestId. Main waits AT MOST
 * `MAIN_WAIT_BUDGET_MS` and then closes regardless — so a renderer that never answers
 * (an older web build, an error page, a crashed page) only ever costs that bound, never
 * a hang. An older shell never sends the request, so the renderer handler is inert
 * there. Both directions are forward- and backward-compatible by construction.
 */

export const GRACEFUL_CLOSE_REQUEST = 'app:will-close'
export const GRACEFUL_CLOSE_ACK = 'app:release-done'

/** How long main waits for the renderer's ack before closing anyway. */
export const MAIN_WAIT_BUDGET_MS = 3500
/** Shorter bound when Windows is shutting down / logging off — it kills us soon. */
export const MAIN_WAIT_BUDGET_SESSION_END_MS = 1200
/** The renderer stops early enough to ack inside main's budget. */
export const RENDERER_RELEASE_BUDGET_MS = 3000
/**
 * Let an in-flight liveview frame finish before the context is deleted. The proven eject
 * path waits ~1s here; the goodbye needs the same breathing room, because deleting the
 * context out from under a preview read is what silently skips `gp_camera_exit` — the
 * shipped 120ms was too short and produced a delay with no camera release (no click).
 */
export const RENDERER_PREVIEW_SETTLE_MS = 700

/** After `gp_camera_exit` is issued, let its USB round-trips land before closing the
 *  device. Under emscripten's asyncify the C++ destructor completes asynchronously
 *  without a handle to await, so a short settle is the only reliable wait. */
export const RENDERER_RELEASE_SETTLE_MS = 300

export interface GracefulCloseRequest {
  requestId: string
  budgetMs: number
}

export interface GracefulCloseAck {
  requestId: string
  /** How many camera instances were released. */
  released: number
  /** Wall time the renderer spent releasing. */
  ms: number
}
